// useResponsiveColumns — picks a column count from a breakpoint map based
// on the current viewport width.
//
// Usage:
//   const cols = useResponsiveColumns();                       // {base:1, sm:2, lg:3} (default)
//   const cols = useResponsiveColumns({base: 2, lg: 3});       // 2 on mobile/tablet, 3 from lg
//   const cols = useResponsiveColumns({base: 2, md: 4});       // matches MediaGallery's
//                                                                  // legacy "columns-2 md:columns-4"
//
// Breakpoints match Tailwind defaults:
//   sm:  ≥ 640px
//   md:  ≥ 768px
//   lg:  ≥ 1024px
//   xl:  ≥ 1280px
//   2xl: ≥ 1536px
//
// SSR-safe: initial render returns the `base` value (no `window` access).
// After mount, syncs to the actual viewport width and re-renders on resize.

import {useEffect, useState} from 'react';

const TAILWIND_BREAKPOINTS = {
    base: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
};

// Resolve the active column count given a breakpoint map and a viewport width.
// Walks the map in ascending breakpoint order, taking the LAST key whose
// minimum width ≤ viewport. Map entries with no matching key fall through
// to `base` (which defaults to 1).
const pickColumns = (map, viewportWidth) => {
    const entries = Object.entries(map)
        .filter(([key]) => key in TAILWIND_BREAKPOINTS)
        .map(([key, value]) => [TAILWIND_BREAKPOINTS[key], value])
        .sort((a, b) => a[0] - b[0]);
    let active = map.base ?? 1;
    for (const [minWidth, cols] of entries) {
        if (viewportWidth >= minWidth) active = cols;
        else break;
    }
    return active;
};

export function useResponsiveColumns(map = {base: 1, sm: 2, lg: 3}) {
    // Initial state: `base` value. SSR-safe (doesn't touch `window`).
    const [columns, setColumns] = useState(() => map.base ?? 1);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const update = () => {
            const w = window.innerWidth;
            const next = pickColumns(map, w);
            setColumns((prev) => (prev !== next ? next : prev));
        };

        // Sync immediately on mount (covers the initial SSR-to-hydration delta).
        update();

        // Attach a resize listener. Use the matchMedia API where breakpoints
        // are distinct so we don't fire on every pixel change — but a simple
        // resize listener with the state-equality guard above is functionally
        // identical and avoids constructing N MediaQueryList objects.
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
        // We intentionally re-run if `map` identity changes; callers should
        // either pass a stable reference or accept the re-attach.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(map)]);

    return columns;
}

export default useResponsiveColumns;
